var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import Lottie from 'lottie-react';
import { FaThumbtack, FaTimes, FaVolumeUp, FaVolumeMute, FaArrowsAlt } from 'react-icons/fa';
import dice3 from '../../assets/adventure/dice3.json';
import { getSounds } from '../../api/sounds';
import { useImmer } from 'use-immer';
import { getCoordinateString, parseCoordinateString } from '../../utils/coordinates';
import { backendUrl, MAP_PADDING_CELLS } from '@common/contracts';
import { getUxPanels } from '../../api/ux-panels';
import { getEventById } from '../../api/level-events';
import { thumb, assetUrl } from '../../utils/img';
import { ABILITY_ICON, KnowledgeReward, PowerReward, MoveIcon, FIELD_MARKER } from '../../utils/gameIcons';
import { approachActionLabel, abilityName } from '../../utils/ui';
import './PhonePreview.scss';
// Normalizes the image URL, prepending backendUrl if it is not a full URL
const normalizeImageUrl = (url) => {
    if (!url)
        return undefined;
    return url.startsWith('http') ? url : `${backendUrl}${url}`;
};
// Result → display, mirroring the game's RollTheDice screen (aitech.tapper).
// The game titles every outcome in WHITE with a glow (never red); only the
// multiplier turns red on a critical failure. Titles are Title-Case here and
// upper-cased by CSS, matching the game's drukBold28 text-transform.
const RESULT_TITLE = {
    FailureX2: 'Critical Failure',
    FailureX1: 'Failure',
    SuccessX1: 'Success',
    SuccessX15: 'Critical Success',
    SuccessX2: 'Critical Success',
    SuccessX3: 'Critical Success',
    SuccessX4: 'Critical Success',
    SuccessX5: 'Critical Success',
};
// Fixed cell pitch (px) for the on-phone field — a real scale that scrolls, not a
// shrunk minimap (the game uses 92px; ~62px keeps ≈4-5 cells across the phone).
const FIELD_PITCH = 62;
// The decorative ring the game keeps around the authored grid, at this preview's
// scale — MAP_PADDING_CELLS cells, exactly what the player sees (contracts.ts,
// MAP GEOMETRY). The board therefore spans the padded grid, and the backdrop maps
// onto it 1:1, so A1 sits where the game puts it instead of at the image corner.
// ACTION_BAR_CLEARANCE is the bottom zone the Go-to/Challenge/Detect buttons
// occupy — the avatar is centred ABOVE it so its neighbours stay tappable.
const FIELD_PAD = MAP_PADDING_CELLS * FIELD_PITCH;
const ACTION_BAR_CLEARANCE = 96;
// Glide the field scroll to a target (used when the avatar steps to a new cell). CSS
// `scroll-behavior: smooth` does the animation in a normal browser; a setTimeout
// fallback snaps to the target if the smooth scroll can't run (e.g. requestAnimationFrame
// is paused for a background/headless tab), so the map ALWAYS ends up re-centered.
function glideFieldScroll(el, settleRef, toLeft, toTop) {
    if (settleRef.current != null) {
        clearTimeout(settleRef.current);
        settleRef.current = null;
    }
    // Remember where the glide began so the fallback can tell "smooth scroll is
    // still animating" from "smooth scroll never ran".
    const fromLeft = el.scrollLeft, fromTop = el.scrollTop;
    el.style.scrollBehavior = 'smooth';
    el.scrollLeft = toLeft;
    el.scrollTop = toTop;
    settleRef.current = window.setTimeout(() => {
        const arrived = Math.abs(el.scrollLeft - toLeft) <= 2 && Math.abs(el.scrollTop - toTop) <= 2;
        const moved = Math.abs(el.scrollLeft - fromLeft) > 2 || Math.abs(el.scrollTop - fromTop) > 2;
        // Snap ONLY when the smooth scroll made no progress (rAF paused in a
        // background/headless tab). A long glide that is still in flight has moved
        // off its start — leave it to finish, or the snap cuts it off as a second
        // jerk (the visible double-jerk after a manual pan).
        if (!arrived && !moved) {
            el.style.scrollBehavior = 'auto';
            el.scrollLeft = toLeft;
            el.scrollTop = toTop;
        }
        settleRef.current = null;
    }, 500);
}
// The play engine emits the SuccessDegree naming ('SuccessX1_5'), while the display
// maps below use the game's PortalChallengeResultType naming ('SuccessX15'). Bridge
// the one that differs so the x1.5 crit gets its title / dice segment / multiplier.
const displayResult = (r) => r === 'SuccessX1_5' ? 'SuccessX15' : r;
// Result → dice3.json segment [start,end], exactly as the game's RollTheDice maps
// PortalChallengeResultType (aitech.tapper). Every roll plays the [0,25] tumble
// first, then the result segment.
const DICE_SEGMENT = {
    FailureX2: [126, 150],
    FailureX1: [51, 75],
    SuccessX1: [26, 50],
    SuccessX15: [76, 100],
    SuccessX2: [101, 125],
    SuccessX3: [151, 175],
    SuccessX4: [176, 200],
    SuccessX5: [201, 224],
};
// The real game dice: dice3.json is an image-sequence Lottie (720x1280 frames served
// from /dice_lottie). Plays the [0,25] roll then the result segment, and reveals the
// result panel after 2s — the same timing the game uses in RollTheDice.tsx.
const DiceRoll = ({ result, onDone }) => {
    const lottieRef = useRef(null);
    const onDoneRef = useRef(onDone);
    onDoneRef.current = onDone;
    const started = useRef(false);
    useEffect(() => {
        var _a, _b;
        const seg = (_b = DICE_SEGMENT[(_a = displayResult(result)) !== null && _a !== void 0 ? _a : 'FailureX1']) !== null && _b !== void 0 ? _b : DICE_SEGMENT.FailureX1;
        // The ref is populated by lottie-react during commit; kick the roll off a
        // tick later so playSegments has a live instance (mirrors the game's click).
        const t0 = setTimeout(() => {
            var _a;
            if (started.current)
                return;
            started.current = true;
            (_a = lottieRef.current) === null || _a === void 0 ? void 0 : _a.playSegments([[0, 25], seg], true);
        }, 60);
        const t1 = setTimeout(() => onDoneRef.current(), 2060);
        return () => { clearTimeout(t0); clearTimeout(t1); };
    }, [result]);
    return (_jsxs("div", { className: "phone-screen pm-adv pm-adv-dice", children: [_jsx("div", { className: "pm-adv-blur" }), _jsx("div", { className: "pm-adv-dice-stage", children: _jsx(Lottie, { className: "pm-adv-dice-lottie", lottieRef: lottieRef, animationData: dice3, autoplay: false, loop: false }) })] }));
};
// Multiplier label per outcome (game RollTheDice.helpers.getMultiplierText).
const MULT_TEXT = {
    FailureX2: 'x2', FailureX1: 'x1', SuccessX1: 'x1', SuccessX15: 'x1.5',
    SuccessX2: 'x2', SuccessX3: 'x3', SuccessX4: 'x4', SuccessX5: 'x5',
};
// A *critical* outcome shows the multiplier plate and slides the reward to the
// right — the game renders these only when the result is neither plain FailureX1
// nor plain SuccessX1 (RollTheDice.tsx: AppearingReward $withoutAnimation).
const isCritOutcome = (rk) => rk !== 'FailureX1' && rk !== 'SuccessX1';
// Reward count-up — the game counts nominal→final, but at EDITOR pace: a debug tool
// gets a quick tick, not the game's 6s-delayed 100ms crawl. Starts almost at once and
// ticks fast so the whole thing lands in well under a second. Fast mode snaps to final.
const REWARD_COUNTUP_DELAY_MS = 120;
const REWARD_COUNTUP_INTERVAL_MS = 28;
function useRewardCountUp(nominal, final, animated) {
    const [val, setVal] = useState(animated ? nominal : final);
    useEffect(() => {
        if (!animated) {
            setVal(final);
            return;
        }
        setVal(nominal);
        let cur = nominal;
        let tick;
        const start = setTimeout(() => {
            tick = setInterval(() => {
                // Scale the step to the remaining distance so a big reward still
                // finishes in ~15 ticks instead of crawling +1 near the bottom.
                const remaining = final - cur;
                const step = Math.max(1, Math.ceil(remaining / 12));
                cur = Math.min(final, cur + step);
                setVal(cur);
                if (cur >= final && tick)
                    clearInterval(tick);
            }, REWARD_COUNTUP_INTERVAL_MS);
        }, REWARD_COUNTUP_DELAY_MS);
        return () => { clearTimeout(start); if (tick)
            clearInterval(tick); };
    }, [nominal, final, animated]);
    return val;
}
// Screen B — the attempt result (game RollTheDice result screen, reproduced 1:1):
// big uppercase title in a gradient band ~70px down, message, then the bottom
// ledger (RewardsTitle · rule · multiplier plate left + reward tile centre→right ·
// rule). The staggered fade/slide timings live in PhonePreview.scss under
// `.pm-adv-resultscreen` (disabled in Fast mode). The reward number counts up.
const ChallengeResultScreen = ({ ls, won, title, message, blur, animated, onDismiss }) => {
    var _a, _b;
    const rk = displayResult(ls.result) || (won ? 'SuccessX1' : 'FailureX1');
    const crit = isCritOutcome(rk);
    const finalVal = won ? ((_a = ls.knowledgeGained) !== null && _a !== void 0 ? _a : 0) : ls.energySpent;
    const nominalVal = (_b = ls.rewardBase) !== null && _b !== void 0 ? _b : finalVal;
    const count = useRewardCountUp(nominalVal, finalVal, animated);
    return (_jsxs("div", { className: "phone-screen pm-adv pm-adv-resultscreen", children: [blur, _jsxs("div", { className: "pm-adv-result", children: [_jsxs("div", { className: "pm-adv-result-head", children: [_jsx("div", { className: "pm-adv-band", children: _jsx("span", { className: "pm-adv-result-text", children: title }) }), _jsx("p", { className: "pm-adv-desc pm-adv-result-msg", children: message })] }), _jsxs("div", { className: "pm-adv-result-foot", children: [_jsxs("div", { className: "pm-adv-rewards-block", children: [_jsx("div", { className: "pm-adv-rewards-title", children: won ? 'Success' : 'Loss' }), _jsx("div", { className: "pm-adv-divider" }), _jsxs("div", { className: "pm-adv-rewards-stage", children: [_jsx("div", { className: "pm-adv-result-line" }), crit && (_jsxs("div", { className: "pm-adv-mult", children: [_jsx("span", { className: "pm-adv-mult-label", children: won ? 'Extra' : 'Minus' }), _jsx("span", { className: `pm-adv-mult-val ${won ? 'win' : 'lose'}`, children: MULT_TEXT[rk] })] })), _jsxs("div", { className: `pm-adv-reward-item${crit ? ' crit' : ''}`, children: [_jsx("img", { className: "pm-adv-reward-img", src: won ? KnowledgeReward : PowerReward, alt: "" }), _jsx("b", { children: count })] })] }), _jsx("div", { className: "pm-adv-divider" })] }), _jsx("button", { className: "pm-adv-cta result-cta", onClick: onDismiss, children: won ? 'Collect' : (ls.retryable ? 'Try again' : 'Move on') })] })] })] }));
};
// Functions for coordinate conversion (similar to FloatingPanel)
const viewportToCanvasCoords = (viewportPos, zoom, translate) => {
    return {
        x: (viewportPos.x - translate.x) / zoom,
        y: (viewportPos.y - translate.y) / zoom,
    };
};
const canvasToViewportCoords = (canvasPos, zoom, translate) => {
    return {
        x: canvasPos.x * zoom + translate.x,
        y: canvasPos.y * zoom + translate.y,
    };
};
export const PhonePreview = ({ levelId, locksOutcomeType, selectedNodeCoords, nodes, onLocksOutcomeTypeChange, initialPosition, onClose, zoomLevel, canvasTranslate, onHighlightCells, playView, onAttempt, onMove, onScan, onTransmit, onRestart, nodeTypes, mapWidth, mapHeight, mapBackgroundUrl, animated = true, }) => {
    var _a, _b;
    // State for UX panels
    const [uxPanels, setUxPanels] = useImmer([]);
    const [selectedUxPanel, setSelectedUxPanel] = useState(null);
    // UX-panel modals queued by a play step's show_ux_panel actions (e.g. a modal that
    // fires after a beaten challenge). Shown one-by-one after the result is collected.
    const [playPanelQueue, setPlayPanelQueue] = useState([]);
    // Play preview: the approach picked before "ROLL DICE", and a dismissed result.
    // A cell's approaches are ONE challenge but each is a separate trial, so the pick
    // must name both the trial and the approach within it (game: choose one option).
    const [selectedOption, setSelectedOption] = useState(null);
    // Tracked by the step's STABLE id (not the object) so a re-run with the same actions
    // (e.g. +Power) doesn't resurface a result the player already dismissed.
    const [dismissedStep, setDismissedStep] = useState(null);
    // The attempt step whose dice roll has finished — its result panel then reveals.
    const [diceDoneStep, setDiceDoneStep] = useState(null);
    // The trial (Challenge) is an OVERLAY the player opens by tapping "Challenge" on
    // the field — the game never auto-opens it on arrival. Closed on cell change.
    const [challengeOpen, setChallengeOpen] = useState(false);
    // Field select-to-move (the game's `nextCell`): tap a reachable neighbour to select
    // it, then the "Go to" button confirms the move. null = the standing cell.
    const [selectedCell, setSelectedCell] = useState(null);
    const fieldScrollRef = useRef(null);
    // Drag-to-pan the field (game: the map is draggable). `moved` lets us tell a pan
    // from a tap so a drag never accidentally selects the cell under the cursor.
    const prevPosRef = useRef(null); // last avatar cell — smooth-scroll only on a real step
    const prevStepCountRef = useRef(0); // step count last centered on — glide only when it GREW (a forward step)
    const scrollSettleRef = useRef(null); // pending "snap to target" fallback timer
    const fieldAbsentRef = useRef(true); // was the field un-rendered since the last center? (starts true so the first mount centers). Set whenever an overlay replaced the field — detected by the ref detaching, NOT DOM-node identity, since React can positionally reuse the field's <div> for an overlay's same-index <div> and leave the node in place at scroll 0,0
    // Off by default: grabbing anywhere on the phone drags the whole phone widget
    // (the original behaviour). Toggled on (button by the sound control), a grab
    // inside the field pans the MAP instead.
    const [mapDragMode, setMapDragMode] = useState(false);
    // ── Adventure sounds ────────────────────────────────────────────────────────
    // Event → served /uploads url (seeded once server-side). Mute is persisted and
    // read through a ref so async playback (dice/result) sees the latest value.
    const soundsRef = useRef({});
    const [muted, setMuted] = useState(() => localStorage.getItem('pm-phone-muted') === '1');
    const mutedRef = useRef(muted);
    useEffect(() => { mutedRef.current = muted; }, [muted]);
    const toggleMuted = useCallback(() => setMuted(m => {
        const next = !m;
        localStorage.setItem('pm-phone-muted', next ? '1' : '0');
        return next;
    }), []);
    useEffect(() => {
        let cancelled = false;
        getSounds().then(list => {
            if (cancelled)
                return;
            const map = {};
            for (const s of list)
                map[s.event] = s.url;
            soundsRef.current = map;
        }).catch(() => { });
        return () => { cancelled = true; };
    }, []);
    const playSound = useCallback((event) => {
        if (mutedRef.current)
            return;
        const url = soundsRef.current[event];
        if (!url)
            return;
        try {
            const a = new Audio(assetUrl(url));
            a.volume = 0.7;
            void a.play();
        }
        catch ( /* ignore */_a) { /* ignore */ }
    }, []);
    // Fire the sound for each new play step (dice roll → challenge start; move; scan;
    // transmit). The win/lose + collect sounds fire from the result screens below.
    // Play a step's sound once, keyed by the STABLE step id — a +Power re-run (new
    // object, same id) must NOT re-trigger it. Win/lose fires from the dice's onDone.
    const soundedStepRef = useRef(null);
    const lastStepForSound = (_a = playView === null || playView === void 0 ? void 0 : playView.lastStep) !== null && _a !== void 0 ? _a : null;
    const lastStepSoundId = (_b = playView === null || playView === void 0 ? void 0 : playView.lastStepId) !== null && _b !== void 0 ? _b : null;
    useEffect(() => {
        const ls = lastStepForSound;
        if (!ls || lastStepSoundId == null || lastStepSoundId === soundedStepRef.current)
            return;
        soundedStepRef.current = lastStepSoundId;
        if (ls.error)
            return;
        if (ls.kind === 'attempt')
            playSound(animated ? 'dice_roll' : (ls.passed ? 'win' : 'lose'));
        else if (ls.kind === 'move')
            playSound('move');
        else if (ls.kind === 'scan')
            playSound('detect');
        else if (ls.kind === 'transmit')
            playSound(ls.transmitSuccess ? 'transmit_success' : 'transmit_fail');
    }, [lastStepForSound, lastStepSoundId, animated, playSound]);
    // State for event data cache
    const [eventDataCache, setEventDataCache] = useState(new Map());
    // State for current event data (for scenario mode)
    const [currentEventData, setCurrentEventData] = useState(null);
    // State for grouped actions
    const [actionGroups, setActionGroups] = useState([]);
    const [currentActionIndex, setCurrentActionIndex] = useState(0);
    const [, setCurrentActionDescription] = useState('');
    // State for controlling playback
    const [isPlaying, setIsPlaying] = useState(false);
    // State for the action type filter
    const [actionFilter, setActionFilter] = useState('all');
    // State for positioning (similar to FloatingPanel)
    const [viewportPosition, setViewportPosition] = useState(initialPosition);
    const [canvasPosition, setCanvasPosition] = useState(null);
    // Which gesture is live, if any — drives the cursor only. One source of truth
    // instead of the old isDragging + fieldDragging pair that could both be set.
    const [dragKind, setDragKind] = useState(null);
    const [isPinnedToCanvas, setIsPinnedToCanvas] = useState(false); // Renamed for clarity
    const phoneRef = useRef(null);
    // Function to compute the effective on-screen position (similar to FloatingPanel)
    const getEffectiveScreenPosition = useCallback(() => {
        if (isPinnedToCanvas && canvasPosition) {
            // Compute the on-screen position from the canvas position, zoom, and translation
            return canvasToViewportCoords(canvasPosition, zoomLevel, canvasTranslate);
        }
        // Otherwise use the stored viewport position
        return viewportPosition;
    }, [isPinnedToCanvas, canvasPosition, viewportPosition, zoomLevel, canvasTranslate]);
    const effectiveScreenPosition = getEffectiveScreenPosition();
    // Load UX panels for this level
    React.useEffect(() => {
        const fetchUxPanels = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const panels = yield getUxPanels(levelId);
                setUxPanels(panels.filter(panel => !panel.isArchived));
            }
            catch (error) {
                console.error('Error loading UX panels:', error);
            }
        });
        fetchUxPanels();
    }, [levelId, setUxPanels]);
    // Function for grouping actions by type with filter support
    const groupActionsByType = useCallback((nodeData, outcomeType, filter) => {
        const groups = [];
        if (!(nodeData === null || nodeData === void 0 ? void 0 : nodeData.simpleEvents))
            return groups;
        // Collect all actions for the selected outcome
        const allActions = [];
        for (const event of nodeData.simpleEvents) {
            const outcome = outcomeType === 'success' ? event.success : event.failure;
            allActions.push(...outcome.actions);
        }
        // Filter actions based on the selected type
        const shouldIncludeScenarios = filter === 'all' || filter === 'scenarios';
        const shouldIncludeUx = filter === 'all' || filter === 'ux';
        const shouldIncludeLocks = filter === 'all' || filter === 'locks';
        const shouldIncludeUnlocks = filter === 'all' || filter === 'unlocks';
        // First add scenarios (new order: ALL / SCENARIOS / UX / LOCKS / UNLOCKS)
        if (shouldIncludeScenarios) {
            // Add scenarios (node events)
            if (nodeData.simpleEvents && nodeData.simpleEvents.length > 0) {
                nodeData.simpleEvents.forEach((event, index) => {
                    groups.push({
                        type: 'scenario',
                        description: `Quick Scenario: ${event.eventTitle || `Event ${index + 1}`}`,
                        data: { event }
                    });
                });
            }
        }
        if (shouldIncludeUx) {
            // Create separate steps for each UX panel
            const uxPanelIds = [];
            allActions
                .filter(action => action.action === 'show_ux_panel' && action.uxPanelId)
                .forEach(action => {
                if (!uxPanelIds.includes(action.uxPanelId)) {
                    uxPanelIds.push(action.uxPanelId);
                }
            });
            // Each panel = a separate step
            uxPanelIds.forEach(panelId => {
                var _a;
                const panelName = ((_a = uxPanels.find(p => p.id === panelId)) === null || _a === void 0 ? void 0 : _a.title) || `Panel ${panelId}`;
                groups.push({
                    type: 'show_ux_panel',
                    description: `Show UX Panel: ${panelName}`,
                    data: { panelId: panelId }
                });
            });
        }
        if (shouldIncludeLocks) {
            // Group lock actions (all locks into one step)
            const lockNodes = [];
            allActions
                .filter(action => action.action === 'lock' && action.nodeCode)
                .forEach(action => {
                if (!lockNodes.includes(action.nodeCode)) {
                    lockNodes.push(action.nodeCode);
                }
            });
            if (lockNodes.length > 0) {
                groups.push({
                    type: 'lock',
                    description: `Lock Nodes: ${lockNodes.join(', ')}`,
                    data: { nodeCodes: lockNodes }
                });
            }
        }
        if (shouldIncludeUnlocks) {
            // Group unlock actions (all unlocks into one step)
            const unlockNodes = [];
            allActions
                .filter(action => action.action === 'unlock' && action.nodeCode)
                .forEach(action => {
                if (!unlockNodes.includes(action.nodeCode)) {
                    unlockNodes.push(action.nodeCode);
                }
            });
            if (unlockNodes.length > 0) {
                groups.push({
                    type: 'unlock',
                    description: `Unlock Nodes: ${unlockNodes.join(', ')}`,
                    data: { nodeCodes: unlockNodes }
                });
            }
        }
        return groups;
    }, [uxPanels]);
    // Function to load event data by eventId
    const loadEventData = useCallback((eventId) => __awaiter(void 0, void 0, void 0, function* () {
        // Check cache first
        if (eventDataCache.has(eventId)) {
            return eventDataCache.get(eventId) || null;
        }
        try {
            const eventData = yield getEventById(levelId, eventId);
            // Cache the result
            setEventDataCache(prev => new Map(prev).set(eventId, eventData));
            return eventData;
        }
        catch (error) {
            console.error(`Error loading event data for ${eventId}:`, error);
            return null;
        }
    }), [levelId, eventDataCache]);
    // Preload the event data (title / image / description) for the trials at the
    // avatar's current cell so the play screen can render them without a flash.
    useEffect(() => {
        if (!(playView === null || playView === void 0 ? void 0 : playView.active))
            return;
        for (const t of playView.currentTrials) {
            if (t.eventId && !eventDataCache.has(t.eventId))
                loadEventData(t.eventId);
        }
    }, [playView, eventDataCache, loadEventData]);
    // Reset the picked option whenever the set of open trials changes (moved cell or
    // resolved a trial). If the cell offers exactly one approach across all of its
    // trials, auto-pick it — the game shows no "Select one option" prompt when there
    // is nothing to choose.
    const openTrials = (playView === null || playView === void 0 ? void 0 : playView.active) ? playView.currentTrials.filter(t => t.status === 'NotStarted') : [];
    const cellKey = (playView === null || playView === void 0 ? void 0 : playView.active)
        ? `${playView.position}:${openTrials.map(t => `${t.trialIndex}x${t.approaches.length}`).join(',')}`
        : '';
    useEffect(() => {
        const single = openTrials.length === 1 && openTrials[0].approaches.length === 1;
        setSelectedOption(single ? { trialIndex: openTrials[0].trialIndex, approachIdx: 0 } : null);
        setChallengeOpen(false); // arriving at a new cell shows the field, not the trial
        setSelectedCell(null); // clear the move selection once we've arrived
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellKey]);
    // Keep the avatar centered in the scrolling field. Runs in a LAYOUT effect (before
    // paint) so a re-center never shows a frame at the old scroll — no "knock the map
    // off then restore it" on undo. CLAMPED to the board so it can't reveal void edges.
    useLayoutEffect(() => {
        const el = fieldScrollRef.current;
        // Mark the field ABSENT whenever it isn't mounted — every overlay (trial / dice /
        // result / scan / transmit / ux-panel) is returned INSTEAD of the map by
        // renderPlayScreen, so the field leaves the tree and `fieldScrollRef` detaches to
        // null. Key the remount off THIS (the detached ref), not DOM-node identity: React
        // reconciles the unkeyed `.phone-screen` children positionally, so it can REUSE the
        // field's <div> for an overlay's same-index <div> (e.g. the on-map Transmit result)
        // — the node then survives but its scroll is clamped to 0,0 while showing overlay
        // content. Only the detached ref reliably flags "the map was gone and is now back".
        if (!(playView === null || playView === void 0 ? void 0 : playView.active) || !playView.position || !el) {
            fieldAbsentRef.current = true;
            return;
        }
        const p = parseCoordinateString(playView.position);
        if (!p)
            return;
        const clamp = (v, max) => Math.max(0, Math.min(v, Math.max(0, max)));
        const left = clamp(p.x * FIELD_PITCH + FIELD_PITCH / 2 + FIELD_PAD - el.clientWidth / 2, el.scrollWidth - el.clientWidth);
        // Centre the avatar in the area ABOVE the action bar so its neighbours don't
        // hide under the Go-to/Challenge/Detect buttons.
        const top = clamp(p.y * FIELD_PITCH + FIELD_PITCH / 2 + FIELD_PAD - (el.clientHeight - ACTION_BAR_CLEARANCE) / 2, el.scrollHeight - el.clientHeight);
        // Glide only when the avatar STEPS FORWARD (the step count grew — an engine move).
        // Undo/redo/re-center snap instantly, before paint, so the map never jumps.
        const stepCount = playView.lastStepId ? parseInt(playView.lastStepId, 10) || 0 : 0;
        const positionChanged = prevPosRef.current !== playView.position;
        const isForwardStep = prevPosRef.current != null && positionChanged && stepCount > prevStepCountRef.current;
        // The field is back after being replaced by an overlay (see the absent-flag above):
        // it returns showing its top-left corner (scroll 0,0), so snap to the avatar before
        // paint instead of leaving the map "thrown somewhere weird" after every trial. A
        // manual pan can't survive the field leaving the tree anyway, so nothing is lost.
        const remounted = fieldAbsentRef.current;
        fieldAbsentRef.current = false;
        prevPosRef.current = playView.position;
        prevStepCountRef.current = stepCount;
        const snapToAvatar = () => {
            if (scrollSettleRef.current != null) {
                clearTimeout(scrollSettleRef.current);
                scrollSettleRef.current = null;
            }
            el.style.scrollBehavior = 'auto';
            el.scrollLeft = left;
            el.scrollTop = top;
        };
        if (remounted) {
            snapToAvatar(); // fresh field after an overlay — centre instantly, never glide from the corner
        }
        else if (isForwardStep) {
            glideFieldScroll(el, scrollSettleRef, left, top);
        }
        else if (positionChanged) {
            snapToAvatar(); // undo/redo or the first center
        }
        // else: same field node, avatar didn't move (an overlay toggled without unmount) —
        // leave the user's manual pan alone instead of yanking back to centre.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playView === null || playView === void 0 ? void 0 : playView.position, playView === null || playView === void 0 ? void 0 : playView.active, playView === null || playView === void 0 ? void 0 : playView.lastStepId, challengeOpen, dismissedStep, playPanelQueue.length]);
    // Field panning now lives in the ONE gesture layer near the phone root — see
    // onPhonePointerDown. No document listeners here: they were the source of the
    // stuck-drag and tap-swallowing bugs.
    // Function to get the cells affected by the current action
    const getAffectedCellsForCurrentAction = useCallback(() => {
        if (actionGroups.length === 0)
            return [];
        const currentAction = actionGroups[currentActionIndex];
        if (!currentAction)
            return [];
        if (currentAction.type === 'unlock' || currentAction.type === 'lock') {
            return currentAction.data.nodeCodes || [];
        }
        return [];
    }, [actionGroups, currentActionIndex]);
    // Update the action groups when the node or status changes
    React.useEffect(() => {
        var _a;
        if (!selectedNodeCoords) {
            setActionGroups([]);
            setCurrentActionIndex(0);
            setCurrentActionDescription('');
            setSelectedUxPanel(null);
            // Clear the cell highlight when the node is deselected
            if (onHighlightCells) {
                onHighlightCells([], 'success', false);
            }
            return;
        }
        const coordString = getCoordinateString(selectedNodeCoords.x, selectedNodeCoords.y);
        const nodeData = nodes[coordString];
        if (!(nodeData === null || nodeData === void 0 ? void 0 : nodeData.simpleEvents) || nodeData.simpleEvents.length === 0) {
            setActionGroups([]);
            setCurrentActionIndex(0);
            setCurrentActionDescription('');
            setSelectedUxPanel(null);
            return;
        }
        const groups = groupActionsByType(nodeData, locksOutcomeType, actionFilter);
        // Smart determination of the new index when switching nodes
        let newActionIndex = 0;
        // If Phone Preview is paused, try to find a matching action type in the new node
        if (!isPlaying && actionGroups.length > 0 && currentActionIndex < actionGroups.length) {
            const currentActionType = (_a = actionGroups[currentActionIndex]) === null || _a === void 0 ? void 0 : _a.type;
            if (currentActionType) {
                // Look for an action of the same type in the new groups
                const sameTypeIndex = groups.findIndex(group => group.type === currentActionType);
                if (sameTypeIndex !== -1) {
                    newActionIndex = sameTypeIndex;
                }
                // If no matching type is found, it stays 0 (reset to the start)
            }
        }
        setActionGroups(groups);
        setCurrentActionIndex(newActionIndex);
        // ALWAYS reset the UX panel before setting a new one
        setSelectedUxPanel(null);
        // Configure UX panels ONLY for UX-related filters
        if (groups.length > 0 && newActionIndex < groups.length) {
            const targetAction = groups[newActionIndex];
            if ((targetAction === null || targetAction === void 0 ? void 0 : targetAction.type) === 'show_ux_panel') {
                const foundPanel = uxPanels.find(p => p.id === targetAction.data.panelId);
                setSelectedUxPanel(foundPanel || null);
            }
            else if (actionFilter === 'ux' || actionFilter === 'all') {
                // Look for a UX panel only if the filter is UX or All
                const uxPanelGroup = groups.find(g => g.type === 'show_ux_panel');
                if (uxPanelGroup && uxPanelGroup.data.panelId) {
                    const foundPanel = uxPanels.find(p => p.id === uxPanelGroup.data.panelId);
                    setSelectedUxPanel(foundPanel || null);
                }
            }
            // Force-update the highlight for the selected action of the new node
            if (onHighlightCells && targetAction) {
                if (targetAction.type === 'unlock' || targetAction.type === 'lock') {
                    const affectedCells = targetAction.data.nodeCodes || [];
                    let highlightType = locksOutcomeType;
                    if (targetAction.type === 'unlock') {
                        highlightType = 'unlock';
                    }
                    else if (targetAction.type === 'lock') {
                        highlightType = 'lock';
                    }
                    onHighlightCells(affectedCells, highlightType, isPlaying);
                }
                else {
                    // For non-lock/unlock actions, clear the highlight
                    onHighlightCells([], 'success', false);
                }
            }
        }
        else {
            // If there are no actions, clear the highlight
            if (onHighlightCells) {
                onHighlightCells([], 'success', false);
            }
        }
    }, [selectedNodeCoords, nodes, locksOutcomeType, uxPanels, groupActionsByType, actionFilter, onHighlightCells, isPlaying]);
    // Reset the index when the filter changes
    React.useEffect(() => {
        setCurrentActionIndex(0);
    }, [actionFilter]);
    // Load event data when current action is scenario type
    React.useEffect(() => {
        const currentAction = actionGroups.length > 0 ? actionGroups[currentActionIndex] : null;
        const isScenarioMode = (currentAction === null || currentAction === void 0 ? void 0 : currentAction.type) === 'scenario';
        if (isScenarioMode && currentAction.data.event) {
            const eventId = currentAction.data.event.eventId;
            const loadData = () => __awaiter(void 0, void 0, void 0, function* () {
                const data = yield loadEventData(eventId);
                setCurrentEventData(data);
            });
            loadData();
        }
        else {
            setCurrentEventData(null);
        }
    }, [actionGroups, currentActionIndex, loadEventData]);
    // Update the current action description and the cell highlight
    React.useEffect(() => {
        if (actionGroups.length === 0) {
            setCurrentActionDescription('');
            // IMPORTANT: reset the UX panel when there are no actions
            setSelectedUxPanel(null);
            // IMPORTANT: clear the cell highlight on the map when there are no actions
            if (onHighlightCells) {
                onHighlightCells([], 'success', false);
            }
            return;
        }
        const currentAction = actionGroups[currentActionIndex];
        if (!currentAction) {
            setCurrentActionDescription('');
            setSelectedUxPanel(null);
            return;
        }
        setCurrentActionDescription(currentAction.description);
        // Update the UX panel if this is a show_ux_panel action
        if (currentAction.type === 'show_ux_panel') {
            const foundPanel = uxPanels.find(p => p.id === currentAction.data.panelId);
            setSelectedUxPanel(foundPanel || null);
        }
        else {
            // If the current action is NOT show_ux_panel but there are UX panels among all actions,
            // show the first UX panel found across all actions
            const uxPanelGroup = actionGroups.find(g => g.type === 'show_ux_panel');
            if (uxPanelGroup && uxPanelGroup.data.panelId) {
                const foundPanel = uxPanels.find(p => p.id === uxPanelGroup.data.panelId);
                setSelectedUxPanel(foundPanel || null);
            }
            else {
                setSelectedUxPanel(null);
            }
        }
        // Highlight cells for current action on map
        if (onHighlightCells) {
            const affectedCells = getAffectedCellsForCurrentAction();
            if (affectedCells.length > 0) {
                // Determine the highlight type based on the action type
                const currentGroup = actionGroups[currentActionIndex];
                let highlightType = locksOutcomeType;
                if (currentGroup) {
                    if (currentGroup.type === 'unlock') {
                        highlightType = 'unlock';
                    }
                    else if (currentGroup.type === 'lock') {
                        highlightType = 'lock';
                    }
                    // For UX panels use locksOutcomeType (success/failure)
                }
                onHighlightCells(affectedCells, highlightType, isPlaying);
            }
        }
    }, [currentActionIndex, actionGroups, onHighlightCells, getAffectedCellsForCurrentAction, locksOutcomeType, uxPanels, isPlaying]);
    // Auto-cycle through action groups
    useEffect(() => {
        if (actionGroups.length <= 1 || !isPlaying) {
            return;
        }
        const interval = setInterval(() => {
            setCurrentActionIndex(prev => (prev + 1) % actionGroups.length);
        }, 1500); // Switch every 1.5 seconds
        return () => clearInterval(interval);
    }, [actionGroups.length, isPlaying]);
    // Component for rendering actions with badges
    const renderActionDescription = () => {
        var _a;
        if (actionGroups.length === 0)
            return { actionType: null, actionItems: null };
        const currentAction = actionGroups[currentActionIndex];
        if (!currentAction)
            return { actionType: null, actionItems: null };
        let actionType = null;
        let actionItems = null;
        if (currentAction.type === 'show_ux_panel') {
            const panelName = ((_a = uxPanels.find(p => p.id === currentAction.data.panelId)) === null || _a === void 0 ? void 0 : _a.title) || `Panel ${currentAction.data.panelId}`;
            actionType = _jsx("span", { className: "action-type action-type-ux", children: "Show UX Panel" });
            actionItems = _jsx("span", { className: "action-item", children: panelName });
        }
        else if (currentAction.type === 'unlock') {
            actionType = _jsx("span", { className: "action-type action-type-unlock", children: "Unlock" });
            actionItems = (_jsx(_Fragment, { children: currentAction.data.nodeCodes.map((nodeCode) => (_jsx("span", { className: "action-item", children: nodeCode }, nodeCode))) }));
        }
        else if (currentAction.type === 'lock') {
            actionType = _jsx("span", { className: "action-type action-type-lock", children: "Lock" });
            actionItems = (_jsx(_Fragment, { children: currentAction.data.nodeCodes.map((nodeCode) => (_jsx("span", { className: "action-item", children: nodeCode }, nodeCode))) }));
        }
        else if (currentAction.type === 'scenario') {
            const event = currentAction.data.event;
            actionType = _jsx("span", { className: "action-type action-type-scenario", children: "Quick Scenario" });
            actionItems = _jsx("span", { className: "action-item", children: event.eventTitle || 'Unnamed Event' });
        }
        return { actionType, actionItems };
    };
    const actionContent = renderActionDescription();
    // ── ONE input layer for the whole phone ────────────────────────────────────
    // Every press inside the phone is arbitrated HERE, once, by a single owner:
    //   • a control/CTA (button, link, field)  -> not a gesture at all; it stays a click
    //   • inside the map while map-drag is on  -> pan the map
    //   • anything else                        -> move the phone
    // A press only BECOMES a drag after the pointer travels DRAG_THRESHOLD px; under
    // that it is a tap and the click runs untouched. Pointer capture guarantees the
    // move/up land back here, so no document listeners, nothing to leak, and no state
    // that can stay stuck after the mouse is released outside the window.
    const DRAG_THRESHOLD = 4;
    const gestureRef = useRef(null);
    // Set when a gesture actually moved, so the trailing click on whatever sat under
    // the cursor (a map cell) is swallowed exactly once instead of being mistaken for a tap.
    const suppressClickRef = useRef(false);
    /** Free drag with a safety strip: any horizontal position on screen, any
     *  vertical as long as a grabbable top strip stays visible. */
    const clampToViewport = useCallback((x, y) => {
        const el = phoneRef.current;
        if (!el)
            return { x, y };
        const w = el.offsetWidth;
        // left/top are relative to the OFFSET PARENT, not the viewport, and that parent
        // starts below the toolbar — so clamping against innerWidth/Height directly
        // would leave the phone hanging off the bottom by exactly the parent's offset.
        // Convert the viewport bounds into the parent's coordinate space first.
        const parent = el.offsetParent;
        const pr = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
        // The play phone (~690px) barely fits between the toolbar and the play
        // bar, so clamping it FULLY on screen leaves a few px of vertical travel —
        // it "rides a rail", sliding only horizontally. Instead, treat it like an
        // OS window: drag it anywhere, even mostly off the bottom edge, as long as
        // a grabbable top strip stays visible to pull it back by. (Parking it over
        // the play bar is then the user's own, always-reversible choice.)
        const MIN_VISIBLE_TOP = 120;
        const minX = -pr.left;
        const minY = -pr.top;
        const maxX = Math.max(minX, window.innerWidth - w - pr.left);
        const maxY = Math.max(minY, window.innerHeight - MIN_VISIBLE_TOP - pr.top);
        return {
            x: Math.min(Math.max(x, minX), maxX),
            y: Math.min(Math.max(y, minY), maxY),
        };
    }, []);
    const onPhonePointerDown = useCallback((e) => {
        if (e.button !== 0)
            return;
        const target = e.target;
        if (!target || !phoneRef.current)
            return;
        // A new press starts a new story: never let a suppression armed by an earlier
        // drag survive to eat this gesture's click. (The browser normally fires the
        // trailing click immediately, but nothing guarantees it did.)
        suppressClickRef.current = false;
        // Whatever happens next belongs to the phone, not to the editor canvas behind it.
        e.stopPropagation();
        // Controls and CTAs are pure clicks. Map cells are buttons too, but they double
        // as the pan surface, so they are explicitly NOT treated as chrome.
        const isChrome = !!target.closest('button, a, input, select, textarea, [role="button"]')
            && !target.closest('.pm-field-cell');
        if (isChrome)
            return;
        const el = fieldScrollRef.current;
        const inField = !!target.closest('.pm-field-scroll');
        if (inField && mapDragMode && el) {
            gestureRef.current = {
                kind: 'field', pointerId: e.pointerId, startX: e.clientX, startY: e.clientY,
                sl: el.scrollLeft, st: el.scrollTop, active: false,
            };
        }
        else {
            const pos = getEffectiveScreenPosition();
            gestureRef.current = {
                kind: 'phone', pointerId: e.pointerId, startX: e.clientX, startY: e.clientY,
                originX: pos.x, originY: pos.y, active: false,
            };
        }
        // NOTE: deliberately NO setPointerCapture here. While a pointer is captured the
        // browser retargets the follow-up mouse events (including `click`) at the
        // capturing element, so capturing on press sent every tap's click to the phone
        // root instead of the button or cell under the finger — the action-bar buttons
        // simply stopped responding. Capture is only taken once the gesture really
        // becomes a drag (see onPhonePointerMove), which is the only time it is needed.
    }, [mapDragMode, getEffectiveScreenPosition]);
    const onPhonePointerMove = useCallback((e) => {
        var _a;
        const g = gestureRef.current;
        if (!g || g.pointerId !== e.pointerId)
            return;
        const dx = e.clientX - g.startX;
        const dy = e.clientY - g.startY;
        if (!g.active) {
            if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD)
                return; // still a tap
            g.active = true;
            setDragKind(g.kind);
            // Now that this IS a drag, keep the stream on one element so move/up can't
            // be lost to whatever the cursor passes over.
            (_a = phoneRef.current) === null || _a === void 0 ? void 0 : _a.setPointerCapture(e.pointerId);
            if (g.kind === 'field') {
                // A manual pan must track the cursor, so cancel any pending glide first.
                if (scrollSettleRef.current != null) {
                    clearTimeout(scrollSettleRef.current);
                    scrollSettleRef.current = null;
                }
                if (fieldScrollRef.current)
                    fieldScrollRef.current.style.scrollBehavior = 'auto';
            }
        }
        if (g.kind === 'field') {
            const el = fieldScrollRef.current;
            if (!el)
                return;
            const clamp = (v, max) => Math.max(0, Math.min(v, Math.max(0, max)));
            el.scrollLeft = clamp(g.sl - dx, el.scrollWidth - el.clientWidth);
            el.scrollTop = clamp(g.st - dy, el.scrollHeight - el.clientHeight);
            return;
        }
        if (isPinnedToCanvas) {
            setCanvasPosition(viewportToCanvasCoords({ x: g.originX + dx, y: g.originY + dy }, zoomLevel, canvasTranslate));
        }
        else {
            setViewportPosition(clampToViewport(g.originX + dx, g.originY + dy));
        }
    }, [isPinnedToCanvas, zoomLevel, canvasTranslate, clampToViewport]);
    const endPhoneGesture = useCallback((e) => {
        var _a;
        const g = gestureRef.current;
        if (!g || g.pointerId !== e.pointerId)
            return;
        if (g.active)
            suppressClickRef.current = true; // it was a drag — kill its trailing click
        gestureRef.current = null;
        setDragKind(null);
        if ((_a = phoneRef.current) === null || _a === void 0 ? void 0 : _a.hasPointerCapture(e.pointerId)) {
            phoneRef.current.releasePointerCapture(e.pointerId);
        }
    }, []);
    // Swallow exactly one click after a real drag, so panning the map never also
    // selects the cell the gesture happened to start on.
    const onPhoneClickCapture = useCallback((e) => {
        if (!suppressClickRef.current)
            return;
        suppressClickRef.current = false;
        e.stopPropagation();
        e.preventDefault();
    }, []);
    // Rescue a phone left off-screen — the window shrank, or the phone itself GREW.
    // The second case is the common one: entering play swaps the widget from 200x520 to
    // 290x692, so a position that fitted a moment ago now hangs its bottom (the action
    // bar) off-screen. A ResizeObserver catches that as well as any content-driven
    // resize, which a window-resize listener alone would miss.
    useEffect(() => {
        const reclamp = () => setViewportPosition(p => clampToViewport(p.x, p.y));
        reclamp();
        window.addEventListener('resize', reclamp);
        const el = phoneRef.current;
        const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reclamp) : null;
        if (el && ro)
            ro.observe(el);
        return () => { window.removeEventListener('resize', reclamp); ro === null || ro === void 0 ? void 0 : ro.disconnect(); };
    }, [clampToViewport]);
    // Handler for pin/unpin (similar to FloatingPanel)
    const handlePinToggle = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        setIsPinnedToCanvas(prevIsPinned => {
            const nextIsPinned = !prevIsPinned;
            const currentScreenPos = getEffectiveScreenPosition();
            if (nextIsPinned) {
                // Switch to canvas-pinned mode
                const newCanvasPos = viewportToCanvasCoords(currentScreenPos, zoomLevel, canvasTranslate);
                setCanvasPosition(newCanvasPos);
                setViewportPosition({ x: 0, y: 0 });
            }
            else {
                // Switch to viewport-pinned mode
                setViewportPosition(currentScreenPos);
                setCanvasPosition(null);
            }
            return nextIsPinned;
        });
    }, [getEffectiveScreenPosition, zoomLevel, canvasTranslate]);
    // Functions for controlling playback
    const handlePlayPause = useCallback(() => {
        setIsPlaying(prev => !prev);
    }, []);
    const handlePrevious = useCallback(() => {
        if (actionGroups.length === 0)
            return;
        setCurrentActionIndex(prev => prev === 0 ? actionGroups.length - 1 : prev - 1);
    }, [actionGroups.length]);
    const handleNext = useCallback(() => {
        if (actionGroups.length === 0)
            return;
        setCurrentActionIndex(prev => (prev + 1) % actionGroups.length);
    }, [actionGroups.length]);
    // Energy + knowledge HUD — shown on every play screen so energy spent per move is
    // always visible (mirrors the game's top-right energy bar).
    const renderEnergyHud = () => {
        const pv = playView;
        const pct = pv.startingEnergy > 0 ? Math.max(0, Math.min(100, (pv.energyLeft / pv.startingEnergy) * 100)) : 0;
        return (_jsx("div", { className: "pm-hud-bar", children: _jsxs("div", { className: "pm-hud-energy", children: [_jsx("span", { className: "pm-hud-energy-track", children: _jsx("span", { className: "pm-hud-energy-fill", style: { width: `${pct}%` } }) }), _jsxs("span", { className: "pm-hud-energy-val", children: [pv.energyLeft, _jsxs("i", { children: [" / ", pv.startingEnergy] })] }), _jsx("img", { className: "pm-hud-energy-icon", src: MoveIcon, alt: "" })] }) }));
    };
    // The field on the phone (game-faithful): a fixed-pitch board of round cell tokens
    // that overflows the phone and scrolls (real scale, NOT a shrunk minimap), the
    // avatar cell pulses, per-cell status markers (Detect/Transmit/Completed/…), and
    // tap-to-select a reachable neighbour (the "Go to" button then confirms the move).
    const renderPlayMap = () => {
        var _a;
        const pv = playView;
        const w = mapWidth !== null && mapWidth !== void 0 ? mapWidth : 0, h = mapHeight !== null && mapHeight !== void 0 ? mapHeight : 0;
        const movable = new Set(pv.movableCells);
        const locked = new Set(pv.lockedCells);
        const scanned = new Set(pv.scannedCells);
        const transmitted = new Set(pv.transmittedCells);
        const sel = selectedCell !== null && selectedCell !== void 0 ? selectedCell : pv.position;
        // Per-cell challenge state, from the published challengeStatus map + authored trials.
        const challengeOf = (coord) => {
            var _a, _b;
            const evs = (_b = (_a = nodes[coord]) === null || _a === void 0 ? void 0 : _a.simpleEvents) !== null && _b !== void 0 ? _b : [];
            if (!evs.length)
                return null;
            const st = evs.map((_, i) => { var _a; return (_a = pv.challengeStatus[`${coord}#${i}`]) !== null && _a !== void 0 ? _a : 'NotStarted'; });
            // The cell's approaches are ONE challenge (game): beating ANY of them beats
            // the cell. Once beaten, it's no longer "not started" / "failed".
            const beaten = st.some(s => s === 'Succeded');
            return {
                beaten,
                notStarted: !beaten && st.some(s => s === 'NotStarted'),
                failed: !beaten && st.some(s => s === 'Failed'),
                retryable: evs.some(e => e.allowRetry !== false),
            };
        };
        // The single status marker for a cell (mirrors Field.tsx priority).
        const markerFor = (coord) => {
            if (transmitted.has(coord))
                return FIELD_MARKER.completed;
            if (scanned.has(coord))
                return FIELD_MARKER.transmit;
            const ch = challengeOf(coord);
            const node = nodes[coord];
            if (ch) {
                if (ch.failed)
                    return ch.retryable ? FIELD_MARKER.crossBlue : FIELD_MARKER.crossRed;
                if (ch.notStarted)
                    return ch.retryable ? FIELD_MARKER.blue : FIELD_MARKER.red;
                // Detect marker only on a BEATEN challenge with scannable resources
                // (game gates Detect on Succeded — challenge-less cells never detect).
                if (ch.beaten && (node === null || node === void 0 ? void 0 : node.hasScannableResources))
                    return FIELD_MARKER.detect;
            }
            return null;
        };
        const cells = [];
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const coord = getCoordinateString(x, y);
                const node = nodes[coord];
                if (!node || node.isWall)
                    continue; // only draw real cells (game: adjacent>0)
                const nt = (node.typeId != null && nodeTypes) ? nodeTypes.find(t => t.id === node.typeId) : undefined;
                const icon = nt && nt.name !== 'Empty' ? normalizeImageUrl((_a = nt.image) !== null && _a !== void 0 ? _a : undefined) : undefined;
                const ch = challengeOf(coord);
                const beaten = !!(ch === null || ch === void 0 ? void 0 : ch.beaten); // dim cleared cells (game: brightness 50%)
                const isMovable = movable.has(coord);
                const cls = ['pm-field-cell'];
                if (coord === pv.position)
                    cls.push('avatar');
                else if (coord === sel)
                    cls.push('selected');
                if (isMovable)
                    cls.push('movable');
                if (locked.has(coord))
                    cls.push('locked');
                const mk = markerFor(coord);
                cells.push(_jsxs("button", { type: "button", className: cls.join(' '), style: { left: x * FIELD_PITCH + FIELD_PAD, top: y * FIELD_PITCH + FIELD_PAD, width: FIELD_PITCH, height: FIELD_PITCH }, disabled: pv.loading, onClick: () => {
                        // A gesture that actually moved swallows its own trailing click
                        // in onPhoneClickCapture, so this only ever runs for a real tap.
                        if (coord === pv.position)
                            setSelectedCell(null); // deselect → standing
                        else if (isMovable)
                            setSelectedCell(coord); // pick neighbour for Go to
                    }, children: [_jsx("span", { className: "pm-field-token", children: icon ? _jsx("img", { className: `pm-field-art ${beaten ? 'beaten' : ''}`, src: icon, alt: "", loading: "lazy", draggable: false }) : null }), mk && _jsx("img", { className: `pm-field-marker ${transmitted.has(coord) ? 'done' : ''}`, src: mk, alt: "", draggable: false }), locked.has(coord) && _jsx("img", { className: "pm-field-lock", src: FIELD_MARKER.locked, alt: "", draggable: false })] }, coord));
            }
        }
        const bg = mapBackgroundUrl ? assetUrl(mapBackgroundUrl) : undefined;
        return (_jsx("div", { className: "pm-field-scroll", ref: fieldScrollRef, style: { cursor: mapDragMode ? (dragKind === 'field' ? 'grabbing' : 'grab') : 'default' }, onDragStart: (e) => e.preventDefault(), children: _jsx("div", { className: "pm-field-board", style: {
                    width: Math.max(1, w) * FIELD_PITCH + 2 * FIELD_PAD, height: Math.max(1, h) * FIELD_PITCH + 2 * FIELD_PAD,
                    backgroundImage: bg ? `url(${bg})` : undefined,
                }, children: cells }) }));
    };
    // ── On-map play preview: reproduces the game Adventure trial/result 1:1 ──────
    const renderPlayScreen = () => {
        var _a, _b, _c, _d;
        const pv = playView;
        const bgUrl = pv.position ? thumb(normalizeImageUrl((_a = nodes[pv.position]) === null || _a === void 0 ? void 0 : _a.imageUrl), 512) : undefined;
        const blur = _jsx("div", { className: "pm-adv-blur", style: { backgroundImage: bgUrl ? `url(${bgUrl})` : undefined } });
        // Run over. There is only ONE ending — the power runs out and the Deep Realm
        // takes the Avatar. Reaching the goal is NOT a win (no "Portal Complete"
        // screen): the climax is carried by the authored finale modal on the goal cell,
        // and the run continues from there until the power is gone.
        if (pv.status === 'stranded') {
            return (_jsxs("div", { className: "phone-screen pm-adv pm-adv-resultscreen", children: [blur, _jsxs("div", { className: "pm-adv-result", children: [_jsxs("div", { className: "pm-adv-result-head", children: [_jsx("div", { className: "pm-adv-band", children: _jsx("span", { className: "pm-adv-result-text", children: "Stranded" }) }), _jsx("p", { className: "pm-adv-desc pm-adv-result-msg", children: "Out of energy \u2014 the Deep Realm kept your Avatar." })] }), _jsxs("div", { className: "pm-adv-result-foot", children: [_jsxs("div", { className: "pm-adv-rewards-block", children: [_jsx("div", { className: "pm-adv-rewards-title", children: "Knowledge extracted" }), _jsx("div", { className: "pm-adv-divider" }), _jsx("div", { className: "pm-adv-rewards-row", children: _jsxs("div", { className: "pm-adv-reward-item", children: [_jsx("img", { className: "pm-adv-reward-img", src: KnowledgeReward, alt: "" }), _jsx("b", { children: pv.knowledgeEarned })] }) }), _jsx("div", { className: "pm-adv-divider" })] }), _jsxs("div", { className: "pm-adv-result-actions", children: [_jsx("button", { className: "pm-adv-cta", onClick: (e) => { e.stopPropagation(); onRestart === null || onRestart === void 0 ? void 0 : onRestart(); }, disabled: pv.loading, children: "Run again" }), _jsx("button", { className: "pm-adv-endbtn", onClick: (e) => { e.stopPropagation(); onClose(); }, children: "Leave the Portal" })] })] })] })] }));
        }
        // A cell's approaches are ONE challenge — once ANY is passed, it's beaten and
        // must not be re-offered (the game never re-challenges a cleared cell).
        const cellBeaten = pv.currentTrials.some(t => t.status === 'Succeded');
        // A cell's challenge may offer several approaches, each imported as its own
        // trial. The game shows them ALL in one card ("choose one option"), so flatten
        // every open trial's approaches into a single option list.
        const openTrials = !cellBeaten ? pv.currentTrials.filter(t => t.status === 'NotStarted') : [];
        const options = openTrials.flatMap(t => t.approaches.map((approach, approachIdx) => ({ trial: t, approach, approachIdx })));
        const activeTrial = openTrials[0]; // shared card art / description come from the challenge
        const ls = pv.lastStep;
        // ── Dice roll — plays before the attempt result (Game mode only; Fast skips it). ──
        if (animated && ls && !ls.error && ls.kind === 'attempt' && dismissedStep !== pv.lastStepId && diceDoneStep !== pv.lastStepId) {
            return _jsx(DiceRoll, { result: ls.result, onDone: () => {
                    var _a;
                    setDiceDoneStep((_a = pv.lastStepId) !== null && _a !== void 0 ? _a : null);
                    if (ls.kind === 'attempt' && !ls.error)
                        playSound(ls.passed ? 'win' : 'lose'); // was a separate effect
                } });
        }
        // ── Screen B: attempt result (SUCCESS / FAILURE) — shown after the dice, and
        //    FIRST among the field overlays so a failed attempt is ALWAYS surfaced. ──
        if (ls && !ls.error && ls.kind === 'attempt' && dismissedStep !== pv.lastStepId) {
            const won = !!ls.passed;
            const rk = displayResult(ls.result) || (won ? 'SuccessX1' : 'FailureX1');
            const title = RESULT_TITLE[rk] || (won ? 'Success' : 'Failure');
            const panelId = ls.panelsShown && ls.panelsShown[0];
            const panel = panelId ? uxPanels.find(p => String(p.id) === String(panelId)) : undefined;
            // The AUTHORED outcome line of the approach that was taken wins — that is the
            // fiction the author wrote for this exact success/failure. A fired UX panel is
            // only a fallback (it's a separate lore beat, shown as its own modal after
            // Collect), and the generic string is a last resort for an unwritten outcome.
            const message = ls.outcomeText
                || (panel === null || panel === void 0 ? void 0 : panel.text)
                || (won ? 'The attempt succeeded.' : `The attempt failed${ls.retryable ? ' — you can try again.' : '.'}`);
            return (_jsx(ChallengeResultScreen, { ls: ls, won: won, title: title, message: message, blur: blur, animated: animated, onDismiss: () => {
                    var _a, _b;
                    if (won)
                        playSound('collect');
                    // Surface any modal(s) this outcome fires (game: a UX panel that
                    // pops after the challenge), resolved against the loaded panels.
                    const queued = ((_a = ls.panelsShown) !== null && _a !== void 0 ? _a : []).filter(id => uxPanels.some(p => String(p.id) === String(id)));
                    if (queued.length)
                        setPlayPanelQueue(queued);
                    setDismissedStep((_b = pv.lastStepId) !== null && _b !== void 0 ? _b : null);
                } }));
        }
        // ── UX-panel modal(s) fired by the last outcome (game: a modal that pops after
        //    a beaten challenge). Shown one-by-one; "Continue" advances the queue. ──
        if (playPanelQueue.length > 0) {
            const panel = uxPanels.find(p => String(p.id) === String(playPanelQueue[0]));
            if (panel) {
                const pbg = panel.backgroundImage ? normalizeImageUrl(panel.backgroundImage) : undefined;
                const pcenter = panel.centerImage ? normalizeImageUrl(panel.centerImage) : undefined;
                return (
                // Game ActionModal: the background image fills the screen (cover, NO
                // blur); a bottom gradient scrim darkens only the lower half so the
                // art shows up top and the text stays readable below.
                _jsxs("div", { className: "phone-screen pm-adv pm-adv-uxpanel", style: pbg ? { backgroundImage: `url(${pbg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' } : undefined, children: [_jsx("div", { className: "pm-adv-uxpanel-scrim" }), _jsxs("div", { className: "pm-adv-uxpanel-body", children: [pcenter && _jsx("img", { className: "pm-adv-uxpanel-img", src: pcenter, alt: "" }), panel.title && _jsx("h3", { className: "pm-adv-uxpanel-title", children: panel.title }), panel.text && _jsx("p", { className: "pm-adv-uxpanel-text", children: panel.text }), _jsx("button", { className: "pm-adv-cta", onClick: () => setPlayPanelQueue(q => q.slice(1)), children: "Continue" })] })] }));
            }
        }
        // ── Scan result (Detect) — reveals the current cell's resources (game ScanResultModal). ──
        if (ls && !ls.error && ls.kind === 'scan' && dismissedStep !== pv.lastStepId) {
            const revealed = (_b = ls.scannedResources) !== null && _b !== void 0 ? _b : [];
            const anyMoveable = revealed.some(r => r.dropChancePercent != null);
            return (_jsxs("div", { className: "phone-screen pm-adv pm-adv-scan", children: [blur, _jsx("button", { className: "pm-adv-back", onClick: () => { var _a; return setDismissedStep((_a = pv.lastStepId) !== null && _a !== void 0 ? _a : null); }, title: "Close", children: "\u2039" }), _jsxs("div", { className: "pm-adv-scan-inner", children: [_jsx("div", { className: "pm-adv-band", children: _jsx("span", { className: "pm-adv-result-text", children: "Resources detected" }) }), _jsxs("div", { className: "pm-scan-grid", children: [revealed.map(r => {
                                        const icon = r.iconUrl ? thumb(normalizeImageUrl(r.iconUrl), 128) : undefined;
                                        return (_jsxs("div", { className: `pm-scan-tile ${r.dropChancePercent == null ? 'dim' : ''}`, title: r.name, children: [icon ? _jsx("img", { src: icon, alt: r.name }) : _jsx("span", { className: "pm-scan-noimg" }), r.dropChancePercent != null && _jsxs("span", { className: "pm-scan-pct", children: [r.dropChancePercent, "%"] })] }, r.resourceId));
                                    }), revealed.length === 0 && _jsx("p", { className: "pm-adv-desc", children: "This cell holds no resources." })] }), _jsx("div", { className: "pm-adv-scan-foot", children: _jsx("button", { className: "pm-adv-cta detect-cta", disabled: !anyMoveable || pv.loading, onClick: () => onTransmit === null || onTransmit === void 0 ? void 0 : onTransmit(), children: "Transmit" }) })] })] }));
        }
        // ── Transmit result — the only randomness (game TransmitResultModal). ──
        if (ls && !ls.error && ls.kind === 'transmit' && dismissedStep !== pv.lastStepId) {
            const results = (_c = ls.transmitResults) !== null && _c !== void 0 ? _c : [];
            const won = !!ls.transmitSuccess;
            return (_jsxs("div", { className: "phone-screen pm-adv pm-adv-resultscreen", children: [blur, _jsxs("div", { className: "pm-adv-result", children: [_jsxs("div", { className: "pm-adv-result-head", children: [_jsx("div", { className: "pm-adv-band", children: _jsx("span", { className: "pm-adv-result-text", children: won ? 'Success' : 'Failure' }) }), _jsx("p", { className: "pm-adv-desc pm-adv-result-msg", children: won ? 'Resources transmitted to storage.' : 'Nothing came through this time.' })] }), _jsxs("div", { className: "pm-adv-result-foot", children: [_jsxs("div", { className: "pm-adv-rewards-block", children: [_jsx("div", { className: "pm-adv-rewards-title", children: won ? 'Transmitted' : 'Loss' }), _jsx("div", { className: "pm-adv-divider" }), _jsx("div", { className: "pm-adv-rewards-row", children: results.length ? results.map(r => {
                                                    const icon = r.iconUrl ? thumb(normalizeImageUrl(r.iconUrl), 128) : undefined;
                                                    return (_jsxs("div", { className: "pm-adv-reward-item", title: r.name, children: [icon ? _jsx("img", { className: "pm-adv-reward-img", src: icon, alt: r.name }) : null, _jsx("b", { children: r.count })] }, r.resourceId));
                                                }) : _jsx("div", { className: "pm-adv-none", children: "None" }) }), _jsx("div", { className: "pm-adv-divider" })] }), _jsx("button", { className: "pm-adv-cta result-cta", onClick: () => { var _a; if (won)
                                            playSound('collect'); setDismissedStep((_a = pv.lastStepId) !== null && _a !== void 0 ? _a : null); }, children: won ? 'Collect' : 'Move on' })] })] })] }));
        }
        // ── Trial card OVERLAY — only when the player tapped "Challenge" on the field
        //    (the game never auto-opens the trial on arrival). Back returns to the field.
        //    Every open trial's approaches are listed together as one choose-one card. ──
        if (challengeOpen && activeTrial && options.length > 0) {
            const ev = eventDataCache.get(activeTrial.eventId);
            const cardImg = (ev === null || ev === void 0 ? void 0 : ev.cardImage) ? thumb(normalizeImageUrl(ev.cardImage), 512) : undefined;
            const selOpt = selectedOption
                ? options.find(o => o.trial.trialIndex === selectedOption.trialIndex && o.approachIdx === selectedOption.approachIdx)
                : undefined;
            const sel = (_d = selOpt === null || selOpt === void 0 ? void 0 : selOpt.approach) !== null && _d !== void 0 ? _d : null;
            return (_jsxs("div", { className: "phone-screen pm-adv pm-adv-trial", style: { backgroundImage: cardImg ? `url(${cardImg})` : undefined }, children: [_jsx("div", { className: "pm-adv-fade" }), _jsx("button", { className: "pm-adv-back", onClick: () => setChallengeOpen(false), title: "Back to field", children: "\u2039" }), renderEnergyHud(), _jsxs("div", { className: "pm-adv-content", children: [_jsxs("div", { className: "pm-adv-hero", children: [_jsx("div", { className: "pm-adv-herocard", children: pv.agentImage ? _jsx("img", { src: thumb(pv.agentImage, 360), alt: "", decoding: "async" }) : _jsx("span", { className: "pm-adv-silhouette" }) }), _jsxs("div", { className: "pm-adv-vs", children: ["VS", sel && _jsxs("span", { className: `pm-adv-vs-chance ${sel.oddsPct > 50 ? 'good' : 'bad'}`, children: [sel.oddsPct, "%"] })] }), _jsx("div", { className: "pm-adv-herocard", children: cardImg ? _jsx("img", { src: cardImg, alt: "" }) : _jsx("span", { className: "pm-adv-silhouette enemy" }) })] }), _jsx("h3", { className: "pm-adv-title", children: pv.currentChallengeTitle || (ev === null || ev === void 0 ? void 0 : ev.title) || activeTrial.title }), (ev === null || ev === void 0 ? void 0 : ev.description) && _jsx("p", { className: "pm-adv-desc", children: ev.description }), _jsx("div", { className: "pm-adv-approaches", children: options.map(({ trial, approach: a, approachIdx }) => {
                                    const isSel = (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.trialIndex) === trial.trialIndex && (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.approachIdx) === approachIdx;
                                    const label = abilityName(String(a.ability));
                                    return (_jsxs("button", { type: "button", className: `pm-adv-approach ${isSel ? 'selected' : ''} ${!a.affordable ? 'unaffordable' : ''}`, disabled: !a.affordable || pv.loading, onClick: () => setSelectedOption({ trialIndex: trial.trialIndex, approachIdx }), children: [_jsxs("div", { className: "pm-adv-approach-top", children: [_jsx("img", { className: "pm-adv-approach-icon", src: ABILITY_ICON[a.ability], alt: "" }), _jsxs("div", { className: "pm-adv-approach-labels", children: [_jsx("span", { className: "pm-adv-approach-title", children: label }), _jsx("span", { className: "pm-adv-approach-sub", children: approachActionLabel(a.title, a.ability) })] }), _jsxs("span", { className: "pm-adv-approach-reward", title: "Knowledge reward (base\u2026base\u00D75)", children: [_jsx("img", { src: KnowledgeReward, alt: "" }), _jsxs("b", { children: [a.rewardAmount, "-", a.rewardAmount * 5] })] })] }), _jsxs("div", { className: "pm-adv-approach-chance", children: [_jsxs("span", { className: "pm-adv-power", children: [_jsx("img", { src: MoveIcon, alt: "" }), " ", a.powerCost] }), _jsxs("span", { className: "pm-adv-chance", children: [_jsx("span", { className: "pm-adv-chance-label", children: "CHANCE" }), " ", _jsxs("span", { className: a.oddsPct > 50 ? 'good' : 'bad', children: [a.oddsPct, "%", a.clamped ? '*' : ''] })] })] })] }, `${trial.trialIndex}-${approachIdx}`));
                                }) }), sel && selectedOption ? (
                            // Disabled when the selected option costs more power than is left — the
                            // game does the same, so a doomed attempt can't be fired (and never
                            // records a rejected step into the run history).
                            _jsxs("button", { className: "pm-adv-cta pm-adv-roll-cta", disabled: pv.loading || !sel.affordable, onClick: () => onAttempt === null || onAttempt === void 0 ? void 0 : onAttempt(selectedOption.trialIndex, selectedOption.approachIdx), children: [_jsx("span", { children: "Take a chance" }), _jsx("img", { className: "pm-adv-cta-icon", src: MoveIcon, alt: "" }), _jsx("span", { children: sel.powerCost })] })) : (_jsx("div", { className: "pm-adv-cta-placeholder", children: "Select one option" }))] })] }));
        }
        // ── Walk: the live map on the phone (avatar + reachable cells + tap-to-move) ──
        return (_jsxs("div", { className: "phone-screen pm-adv pm-adv-map", children: [renderEnergyHud(), renderPlayMap(), (() => {
                    const selIsNeighbour = selectedCell != null && selectedCell !== pv.position && pv.movableCells.includes(selectedCell);
                    return (_jsxs("div", { className: "pm-adv-actions", children: [!!pv.position && pv.scannedCells.includes(pv.position) && !pv.transmittedCells.includes(pv.position) && pv.currentCellResources.length > 0 && (_jsx("div", { className: "pm-adv-resfound", children: pv.currentCellResources.map(r => {
                                    const icon = r.iconUrl ? thumb(normalizeImageUrl(r.iconUrl), 96) : undefined;
                                    return (_jsxs("span", { className: `pm-adv-resfound-item ${r.dropChancePercent == null ? 'dim' : ''}`, title: r.name, children: [icon ? _jsx("img", { src: icon, alt: "" }) : _jsx("span", { className: "pm-adv-resfound-noimg" }), r.dropChancePercent != null && _jsxs("b", { children: [r.dropChancePercent, "%"] })] }, r.resourceId));
                                }) })), selIsNeighbour && selectedCell ? (_jsxs("button", { className: "pm-adv-cta", disabled: pv.loading, onClick: () => onMove === null || onMove === void 0 ? void 0 : onMove(selectedCell), children: ["Go to ", _jsxs("span", { className: "pm-adv-cta-cost", children: [_jsx("img", { src: MoveIcon, alt: "" }), " ", pv.moveEnergyCost] })] })) : (_jsxs(_Fragment, { children: [activeTrial && (_jsx("button", { className: "pm-adv-cta challenge-cta", disabled: pv.loading, onClick: () => { playSound('challenge'); setChallengeOpen(true); }, children: "Challenge" })), pv.canScan && (_jsxs("button", { className: "pm-adv-cta detect-cta", disabled: pv.loading, onClick: () => onScan === null || onScan === void 0 ? void 0 : onScan(), children: [_jsxs("span", { className: "pm-adv-cta-badge", children: ["x", pv.scanCountLeft] }), "Detect"] })), pv.canTransmit && (_jsx("button", { className: "pm-adv-cta detect-cta", disabled: pv.loading, onClick: () => onTransmit === null || onTransmit === void 0 ? void 0 : onTransmit(), children: "Transmit" }))] }))] }));
                })()] }));
    };
    // Render phone preview
    const renderPhonePreview = () => {
        // Check the type of the current action
        const currentAction = actionGroups.length > 0 ? actionGroups[currentActionIndex] : null;
        const isScenarioMode = (currentAction === null || currentAction === void 0 ? void 0 : currentAction.type) === 'scenario';
        const isLockUnlockMode = (currentAction === null || currentAction === void 0 ? void 0 : currentAction.type) === 'lock' || (currentAction === null || currentAction === void 0 ? void 0 : currentAction.type) === 'unlock';
        if (isScenarioMode && currentAction.data.event) {
            // Scenario (event) display mode
            const event = currentAction.data.event;
            const coordString = selectedNodeCoords ? getCoordinateString(selectedNodeCoords.x, selectedNodeCoords.y) : null;
            const nodeData = coordString && nodes[coordString] ? nodes[coordString] : null;
            return (_jsxs("div", { className: "phone-screen", children: [_jsx("div", { className: "phone-background", style: {
                            backgroundImage: (nodeData === null || nodeData === void 0 ? void 0 : nodeData.imageUrl)
                                ? `url(${normalizeImageUrl(nodeData.imageUrl)})`
                                : undefined
                        } }), _jsxs("div", { className: "phone-modal-content", children: [_jsx("div", { className: "phone-center-image", children: (currentEventData === null || currentEventData === void 0 ? void 0 : currentEventData.cardImage) ? (_jsx("img", { src: normalizeImageUrl(currentEventData.cardImage), alt: "Event image" })) : (_jsx("div", { className: "event-image-placeholder", children: "Event Image" })) }), _jsx("h3", { className: "phone-title", children: event.eventTitle }), _jsx("p", { className: "phone-text", children: event.eventDescription })] })] }));
        }
        // For lock/unlock actions show plain text
        if (isLockUnlockMode && currentAction) {
            const nodeCount = currentAction.data.nodeCodes.length;
            const actionText = currentAction.type === 'lock' ? 'locked' : 'unlocked';
            const nodeWord = nodeCount === 1 ? 'Node' : 'Nodes';
            return (_jsx("div", { className: "phone-screen", children: _jsx("div", { className: "no-ux-panel", children: _jsxs("p", { children: [nodeCount, " ", nodeWord, " will be ", actionText] }) }) }));
        }
        // Show the empty state only if there are no actions or no selected node
        if (actionGroups.length === 0 || !selectedNodeCoords) {
            let emptyMessage = "";
            let emptyDescription = "";
            if (!selectedNodeCoords) {
                emptyMessage = "No Node Selected";
                emptyDescription = "Select a node to preview";
            }
            else {
                // Get information about actions in both outcomes for more accurate messages
                const coordString = getCoordinateString(selectedNodeCoords.x, selectedNodeCoords.y);
                const nodeData = nodes[coordString];
                let hasInSuccess = false;
                let hasInFailure = false;
                if (nodeData === null || nodeData === void 0 ? void 0 : nodeData.simpleEvents) {
                    for (const event of nodeData.simpleEvents) {
                        const successActions = event.success.actions;
                        const failureActions = event.failure.actions;
                        switch (actionFilter) {
                            case 'scenarios':
                                // Scenarios always exist if there are simpleEvents
                                hasInSuccess = true;
                                hasInFailure = true;
                                break;
                            case 'ux':
                                hasInSuccess = successActions.some(a => a.action === 'show_ux_panel');
                                hasInFailure = failureActions.some(a => a.action === 'show_ux_panel');
                                break;
                            case 'locks':
                                hasInSuccess = successActions.some(a => a.action === 'lock');
                                hasInFailure = failureActions.some(a => a.action === 'lock');
                                break;
                            case 'unlocks':
                                hasInSuccess = successActions.some(a => a.action === 'unlock');
                                hasInFailure = failureActions.some(a => a.action === 'unlock');
                                break;
                        }
                        if (hasInSuccess || hasInFailure)
                            break;
                    }
                }
                // Determine the message depending on the current filter and outcome
                const outcomeText = locksOutcomeType === 'success' ? 'success' : 'failure';
                const hasInCurrentOutcome = locksOutcomeType === 'success' ? hasInSuccess : hasInFailure;
                const hasInOppositeOutcome = locksOutcomeType === 'success' ? hasInFailure : hasInSuccess;
                switch (actionFilter) {
                    case 'scenarios':
                        emptyMessage = "No Scenarios";
                        emptyDescription = "Node has no Quick Scenarios";
                        break;
                    case 'ux':
                        if (!hasInCurrentOutcome && !hasInOppositeOutcome) {
                            emptyMessage = "No UX Panels";
                            emptyDescription = "Node has no UX Panel actions";
                        }
                        else if (!hasInCurrentOutcome) {
                            emptyMessage = "No UX Panels";
                            emptyDescription = `No UX Panel actions on ${outcomeText}`;
                        }
                        break;
                    case 'locks':
                        if (!hasInCurrentOutcome && !hasInOppositeOutcome) {
                            emptyMessage = "No Locks";
                            emptyDescription = "Node has no Lock actions";
                        }
                        else if (!hasInCurrentOutcome) {
                            emptyMessage = "No Locks";
                            emptyDescription = `No Lock actions on ${outcomeText}`;
                        }
                        break;
                    case 'unlocks':
                        if (!hasInCurrentOutcome && !hasInOppositeOutcome) {
                            emptyMessage = "No Unlocks";
                            emptyDescription = "Node has no Unlock actions";
                        }
                        else if (!hasInCurrentOutcome) {
                            emptyMessage = "No Unlocks";
                            emptyDescription = `No Unlock actions on ${outcomeText}`;
                        }
                        break;
                    default:
                        emptyMessage = "No Actions";
                        emptyDescription = "Node has no interactive content";
                }
            }
            return (_jsx("div", { className: "phone-screen", children: _jsxs("div", { className: "no-ux-panel", children: [_jsx("p", { children: emptyMessage }), _jsx("span", { children: emptyDescription })] }) }));
        }
        if (!selectedUxPanel) {
            // If there are actions but no UX panel, that does not always mean a UX panel is absent
            // The current filter needs to be analyzed
            if (actionFilter === 'ux' || actionFilter === 'all') {
                return (_jsx("div", { className: "phone-screen", children: _jsxs("div", { className: "no-ux-panel", children: [_jsx("p", { children: "No UX Panel" }), _jsx("span", { children: "Node has UX Panel actions but no panel data" })] }) }));
            }
            else {
                // For other filters (locks, unlocks, scenarios) do not show "No UX Panel"
                // Fall back to normal rendering without a UX panel
                const currentAction = actionGroups.length > 0 ? actionGroups[currentActionIndex] : null;
                if ((currentAction === null || currentAction === void 0 ? void 0 : currentAction.type) === 'scenario') {
                    // Scenario - render as before
                    const event = currentAction.data.event;
                    const coordString = selectedNodeCoords ? getCoordinateString(selectedNodeCoords.x, selectedNodeCoords.y) : null;
                    const nodeData = coordString && nodes[coordString] ? nodes[coordString] : null;
                    return (_jsxs("div", { className: "phone-screen", children: [_jsx("div", { className: "phone-background", style: {
                                    backgroundImage: (nodeData === null || nodeData === void 0 ? void 0 : nodeData.imageUrl)
                                        ? `url(${normalizeImageUrl(nodeData.imageUrl)})`
                                        : undefined
                                } }), _jsxs("div", { className: "phone-modal-content", children: [_jsx("div", { className: "phone-center-image", children: (currentEventData === null || currentEventData === void 0 ? void 0 : currentEventData.cardImage) ? (_jsx("img", { src: normalizeImageUrl(currentEventData.cardImage), alt: "Event image" })) : (_jsx("div", { className: "event-image-placeholder", children: "Event Image" })) }), _jsx("h3", { className: "phone-title", children: event.eventTitle }), _jsx("p", { className: "phone-text", children: event.eventDescription })] })] }));
                }
                else {
                    // For locks, unlocks, and others - show action information
                    return (_jsx("div", { className: "phone-screen", children: _jsxs("div", { className: "no-ux-panel", children: [_jsx("p", { children: (currentAction === null || currentAction === void 0 ? void 0 : currentAction.description) || 'Action Info' }), _jsxs("span", { children: ["Preview for ", actionFilter, " action"] })] }) }));
                }
            }
        }
        const isModal = selectedUxPanel.panelType === 'modal';
        return (_jsx("div", { className: "phone-screen", children: isModal ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "phone-screen-background" }), _jsxs("div", { className: "phone-modal-window", children: [_jsx("div", { className: "phone-modal-background", style: {
                                    backgroundImage: selectedUxPanel.backgroundImage
                                        ? `url(${normalizeImageUrl(selectedUxPanel.backgroundImage)})`
                                        : undefined
                                } }), _jsx("div", { className: "phone-modal-close" }), _jsxs("div", { className: "phone-modal-content", children: [selectedUxPanel.centerImage && (_jsx("div", { className: "phone-center-image", children: _jsx("img", { src: normalizeImageUrl(selectedUxPanel.centerImage), alt: "Center image" }) })), _jsx("h3", { className: "phone-title", children: selectedUxPanel.title }), _jsx("p", { className: "phone-text", children: selectedUxPanel.text })] })] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "phone-background", style: {
                            backgroundImage: selectedUxPanel.backgroundImage
                                ? `url(${normalizeImageUrl(selectedUxPanel.backgroundImage)})`
                                : undefined
                        } }), _jsxs("div", { className: "phone-modal-content", children: [selectedUxPanel.centerImage && (_jsx("div", { className: "phone-center-image", children: _jsx("img", { src: normalizeImageUrl(selectedUxPanel.centerImage), alt: "Center image" }) })), _jsx("h3", { className: "phone-title", children: selectedUxPanel.title }), _jsx("p", { className: "phone-text", children: selectedUxPanel.text })] })] })) }));
    };
    return (_jsxs("div", { ref: phoneRef, className: `phone-preview-widget ${(playView === null || playView === void 0 ? void 0 : playView.active) ? 'phone-play' : ''} ${!animated ? 'pm-fast' : ''}`, style: {
            position: 'absolute',
            left: `${effectiveScreenPosition.x}px`,
            top: `${effectiveScreenPosition.y}px`,
            cursor: dragKind ? 'grabbing' : 'grab',
            // Above the toolbar/overlay panels (9999) so the phone's own controls
            // (sound, map-drag, back) stay clickable even when the phone is dragged
            // up over the Overlay/Zoom panels. Still below the play bar (10001).
            zIndex: 10000
        }, onPointerDown: onPhonePointerDown, onPointerMove: onPhonePointerMove, onPointerUp: endPhoneGesture, onPointerCancel: endPhoneGesture, onClickCapture: onPhoneClickCapture, 
        // The editor canvas pans on its own onMouseDown (LevelEditor), and a
        // mousedown is a SEPARATE event from the pointerdown above — stopping one
        // does not stop the other. Without this, every gesture inside the phone
        // (moving the phone, panning its map) also dragged the editor map underneath.
        onMouseDown: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "phone-controls", children: [!(playView === null || playView === void 0 ? void 0 : playView.active) && (_jsx("button", { className: `control-btn pin-btn ${isPinnedToCanvas ? 'pinned' : ''}`, onClick: handlePinToggle, title: isPinnedToCanvas ? "Unpin from Canvas" : "Pin to Canvas", children: _jsx(FaThumbtack, {}) })), (playView === null || playView === void 0 ? void 0 : playView.active) ? (
                    // During a play run the phone is closed via the play bar's Exit; this
                    // slot is the sound on/off toggle instead of a redundant ✕, plus a
                    // map-drag mode toggle (off = grabbing the phone moves the phone).
                    _jsxs(_Fragment, { children: [_jsx("button", { className: `control-btn mapdrag-btn ${mapDragMode ? 'active' : ''}`, onClick: (e) => { e.stopPropagation(); setMapDragMode((v) => !v); }, title: mapDragMode ? 'Map drag: on (tap to move the phone instead)' : 'Map drag: off (tap to pan the map)', children: _jsx(FaArrowsAlt, {}) }), _jsx("button", { className: `control-btn mute-btn ${muted ? 'muted' : ''}`, onClick: (e) => { e.stopPropagation(); toggleMuted(); }, title: muted ? 'Unmute' : 'Mute', children: muted ? _jsx(FaVolumeMute, {}) : _jsx(FaVolumeUp, {}) })] })) : (_jsx("button", { className: "control-btn close-btn", onClick: (e) => { e.stopPropagation(); onClose(); }, title: "Close", children: _jsx(FaTimes, {}) }))] }), _jsx("div", { className: "phone-frame", style: { pointerEvents: 'auto' }, children: (playView === null || playView === void 0 ? void 0 : playView.active) ? renderPlayScreen() : renderPhonePreview() }), selectedNodeCoords && !(playView === null || playView === void 0 ? void 0 : playView.active) && (_jsxs("div", { className: "progress-controls-container", children: [_jsxs("div", { className: "filters-container", children: [_jsxs("div", { className: "filter-buttons action-type-filters", children: [_jsx("button", { className: `filter-btn ${actionFilter === 'all' ? 'active' : ''}`, onClick: () => setActionFilter('all'), children: "All" }), _jsx("button", { className: `filter-btn ${actionFilter === 'scenarios' ? 'active' : ''}`, onClick: () => setActionFilter('scenarios'), children: "Scenarios" }), _jsx("button", { className: `filter-btn ${actionFilter === 'ux' ? 'active' : ''}`, onClick: () => setActionFilter('ux'), children: "UX" }), _jsx("button", { className: `filter-btn ${actionFilter === 'locks' ? 'active' : ''}`, onClick: () => setActionFilter('locks'), children: "Locks" }), _jsx("button", { className: `filter-btn ${actionFilter === 'unlocks' ? 'active' : ''}`, onClick: () => setActionFilter('unlocks'), children: "Unlocks" })] }), _jsxs("div", { className: "filter-buttons outcome-filters", children: [_jsx("button", { className: `filter-btn outcome-btn ${locksOutcomeType === 'success' ? 'active success' : ''}`, onClick: () => onLocksOutcomeTypeChange('success'), children: "Success" }), _jsx("button", { className: `filter-btn outcome-btn ${locksOutcomeType === 'failure' ? 'active failure' : ''}`, onClick: () => onLocksOutcomeTypeChange('failure'), children: "Failure" })] })] }), actionGroups.length > 0 && (_jsxs("div", { className: "progress-controls", children: [_jsx("button", { className: "control-btn control-btn-prev", onClick: handlePrevious, title: "Previous step", children: "\u2039" }), _jsx("button", { className: `control-btn control-btn-play ${isPlaying ? 'playing' : 'paused'}`, onClick: handlePlayPause, title: isPlaying ? 'Pause' : 'Play', children: isPlaying ? '❚❚' : '▶' }), _jsx("button", { className: "control-btn control-btn-next", onClick: handleNext, title: "Next step", children: "\u203A" })] }))] })), actionGroups.length > 0 && !(playView === null || playView === void 0 ? void 0 : playView.active) && (_jsxs("div", { className: "action-description", children: [_jsx("div", { className: "progress-bar", children: _jsx("div", { className: "progress-fill", style: {
                                width: `${((currentActionIndex + 1) / actionGroups.length) * 100}%`
                            } }) }), actionContent.actionType, actionContent.actionItems && (_jsx("div", { className: "action-items", children: actionContent.actionItems }))] }))] }));
};
